import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { lookupInvite, type InviteLookup } from '../../lib/services/invitesApi';
import { InviteAcceptForm, ALREADY_REGISTERED_MESSAGE } from '../../components/auth/InviteAcceptForm';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';

// Messaggio unico per QUALSIASI errore di lookup (token inesistente, scaduto,
// revocato, già accettato): non distinguiamo i casi per non dare informazioni
// utili a un attaccante che enumera token.
const LOOKUP_ERROR_MESSAGE = 'Invito non valido o scaduto. Chiedi un nuovo invito all\'amministratore.';

// Banner mostrato quando si torna da OAuthCallback dopo un accept-oauth fallito
// (vedi ramo invite in OAuthCallback.tsx).
function bannerForQueryError(error: string | null, inviteEmail: string | undefined): string | null {
  if (!error) return null;
  if (error === 'email_mismatch') {
    return inviteEmail
      ? `Devi accedere con l'account ${inviteEmail}.`
      : 'Devi accedere con l\'account a cui è stato inviato l\'invito.';
  }
  if (error === 'accept_failed') {
    return 'Non è stato possibile completare l\'accettazione dell\'invito. Riprova.';
  }
  if (error === 'already_registered') {
    return ALREADY_REGISTERED_MESSAGE;
  }
  return null;
}

/**
 * Pagina pubblica /invite/:token — accettazione invito team (vedi Task 7/8/9 del
 * progetto "Inviti Team"). Fa il lookup del token via core.flusia.it, poi mostra
 * il form di accettazione (password oppure social) o un messaggio d'errore unico
 * se il token non è valido/scaduto.
 */
export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InviteLookup | null>(null);
  const [lookupFailed, setLookupFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token) {
        setLookupFailed(true);
        setLoading(false);
        return;
      }
      try {
        const result = await lookupInvite(token);
        if (!cancelled) setInvite(result);
      } catch (err) {
        console.error('[InviteAcceptPage] lookupInvite failed:', err);
        if (!cancelled) setLookupFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [token]);

  const queryError = searchParams.get('error');
  const banner = bannerForQueryError(queryError, invite?.email);

  return (
    <RegisterSplitLayout visual={{ startCard: 'wiki' }}>
      <div className="mb-8">
        {loading ? (
          <div className="flex items-center gap-2 text-[#787774] py-4">
            <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
            <span className="text-sm">Verifica invito in corso...</span>
          </div>
        ) : lookupFailed || !invite || !token ? (
          <>
            <h1 className="text-2xl font-bold text-[#37352F] mb-1">Invito non disponibile</h1>
            <p className="text-sm text-[#787774]">{LOOKUP_ERROR_MESSAGE}</p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-[#37352F] mb-1">
              {invite.org_name} ti ha invitato su Flusia
            </h1>
            <p className="text-sm text-[#787774]">
              {invite.email} · Ruolo: {invite.role_name}
            </p>
          </>
        )}
      </div>

      {!loading && invite && token && (
        <>
          {banner && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">{banner}</p>
            </div>
          )}
          <InviteAcceptForm token={token} invite={invite} />
        </>
      )}
    </RegisterSplitLayout>
  );
}

export default InviteAcceptPage;
